import { useEffect, useState } from 'react';
import { Mail, MousePointer, Eye, Send, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../../components/ui/Spinner';

interface DayMetric {
  metric_date: string;
  channel: string;
  total_sent: number;
  total_delivered: number;
  total_opened: number;
  total_clicked: number;
  total_bounced: number;
  total_failed: number;
  email_open_rate: number;
  email_ctr: number;
  push_open_rate: number;
}

interface KpiCard {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  color?: string;
}

export function AdminEmailMetricsPage() {
  const [rows,    setRows]    = useState<DayMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('engagement_metrics')
      .select('*')
      .order('metric_date', { ascending: false })
      .limit(90);
    if (err) { setError(err.message); setLoading(false); return; }
    setRows((data ?? []) as DayMetric[]);
    setLoading(false);
  }

  const emailRows = rows.filter(r => r.channel === 'email');

  const totalSent      = emailRows.reduce((s, r) => s + (r.total_sent      ?? 0), 0);
  const totalOpened    = emailRows.reduce((s, r) => s + (r.total_opened    ?? 0), 0);
  const totalClicked   = emailRows.reduce((s, r) => s + (r.total_clicked   ?? 0), 0);
  const totalBounced   = emailRows.reduce((s, r) => s + (r.total_bounced   ?? 0), 0);
  const totalFailed    = emailRows.reduce((s, r) => s + (r.total_failed    ?? 0), 0);
  const avgOpenRate    = totalSent > 0 ? ((totalOpened  / totalSent) * 100).toFixed(1) : '-';
  const avgCtr         = totalSent > 0 ? ((totalClicked / totalSent) * 100).toFixed(1) : '-';
  const bounceRate     = totalSent > 0 ? ((totalBounced / totalSent) * 100).toFixed(1) : '-';

  const kpis: KpiCard[] = [
    { label: 'Emails enviados (90d)', value: totalSent.toLocaleString('pt-BR'),    icon: <Send size={20} /> },
    { label: 'Taxa de abertura',      value: `${avgOpenRate}%`,                    icon: <Eye size={20} />,         color: '#22c55e' },
    { label: 'Taxa de clique (CTR)',  value: `${avgCtr}%`,                         icon: <MousePointer size={20} />, color: '#3b82f6' },
    { label: 'Taxa de bounce',        value: `${bounceRate}%`,                     icon: <AlertCircle size={20} />,  color: totalBounced > 0 ? '#f59e0b' : undefined },
    { label: 'Falhas de envio',       value: totalFailed.toLocaleString('pt-BR'),  icon: <Mail size={20} />,         color: totalFailed > 0 ? '#ef4444' : undefined },
  ];

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: 'var(--text-22)', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          Métricas de Email
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0.25rem 0 0' }}>
          Performance dos últimos 90 dias
        </p>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 0' }}>
          <Spinner size="lg" />
        </div>
      )}

      {error && (
        <div className="card" style={{ borderColor: 'rgba(239,68,68,0.3)', backgroundColor: 'rgba(239,68,68,0.06)', marginBottom: '1.5rem' }}>
          <p style={{ color: '#ef4444', margin: 0, fontSize: 'var(--text-label)' }}>
            Erro ao carregar: {error}
          </p>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            {kpis.map(k => (
              <div key={k.label} className="card" style={{ padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: k.color ?? 'var(--text-muted)' }}>
                  {k.icon}
                  <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{k.label}</span>
                </div>
                <p style={{ fontSize: '1.75rem', fontWeight: 700, color: k.color ?? 'var(--text-primary)', margin: 0, lineHeight: 1 }}>
                  {k.value}
                </p>
              </div>
            ))}
          </div>

          {/* Daily table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-subtle)' }}>
              <h2 style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0, fontSize: 'var(--text-label)' }}>
                Histórico diário
              </h2>
            </div>

            {rows.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)', margin: 0 }}>Nenhum dado encontrado.</p>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0.5rem 0 0' }}>
                  Os dados aparecem após o primeiro envio ser registrado no <code>notification_log</code>.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)', backgroundColor: 'var(--bg-surface)' }}>
                      {['Data', 'Canal', 'Enviados', 'Entregues', 'Abertos', 'Clicados', 'Bounces', 'Falhas', 'Open Rate', 'CTR'].map(h => (
                        <th key={h} style={{ padding: '0.625rem 1rem', textAlign: 'left', fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const openRate = r.total_sent > 0 ? ((r.total_opened / r.total_sent) * 100).toFixed(1) : '-';
                      const ctr      = r.total_sent > 0 ? ((r.total_clicked / r.total_sent) * 100).toFixed(1) : '-';
                      return (
                        <tr key={`${r.metric_date}-${r.channel}-${i}`} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                            {new Date(r.metric_date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td style={{ padding: '0.625rem 1rem' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                              backgroundColor: r.channel === 'email' ? 'rgba(59,130,246,0.1)' : 'rgba(34,197,94,0.1)',
                              color: r.channel === 'email' ? '#3b82f6' : '#22c55e',
                            }}>
                              {r.channel}
                            </span>
                          </td>
                          <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-primary)' }}>{r.total_sent ?? 0}</td>
                          <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-primary)' }}>{r.total_delivered ?? 0}</td>
                          <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-primary)' }}>{r.total_opened ?? 0}</td>
                          <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-primary)' }}>{r.total_clicked ?? 0}</td>
                          <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: r.total_bounced > 0 ? '#f59e0b' : 'var(--text-primary)' }}>{r.total_bounced ?? 0}</td>
                          <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: r.total_failed > 0 ? '#ef4444' : 'var(--text-primary)' }}>{r.total_failed ?? 0}</td>
                          <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-primary)' }}>{openRate}{openRate !== '-' ? '%' : ''}</td>
                          <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-primary)' }}>{ctr}{ctr !== '-' ? '%' : ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AdminEmailMetricsPage;
